import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { faArrowLeft, faChevronRight, faSave } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { MeasureData as TransformedToMeasureData } from "@treegram/viewer-core";
import { MeasureFragmentConverter } from "@treegram/converters-measure-fragment";
import { FragmentLoader } from "@treegram/loaders-fragment";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context.ts";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";
import useStoreyMeasuresContext from "./context/measures/use-storey-measures-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useProjectFormNavigationContext from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";

import { useProjectFormHeadingLayoutEffect } from "../../../../context/project-heading/use-project-heading-context.ts";

import ViewerItemsVisibilityProvider from "@/services/context/viewer/items-visibility/viewer-items-visibility-provider.tsx";
import ModelingToolsContextProvider from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import ViewerContextProvider from "@/services/context/viewer/instance/viewer-context-provider.tsx";
import StoreyMeasuresProviders from "./context/measures/storey-measures-providers.tsx";
import StoreyIndicatorsProvider from "./context/indicators/storey-indicators-provider.tsx";
import StoreyMeasurementProvider from "./context/measurement/storey-measurement-provider.tsx";
import useStoreyMeasurementContext from "./context/measurement/use-storey-measurement-context.ts";
import useStoreyMeasuresLoaders from "./hooks/use-storey-measures-loaders.ts";
import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import useMeasureProjectActions from "../../hooks/use-measure-project-actions.tsx";

import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import Button from "@/components/buttons/button";

import Measure3dViewerContainer, { Measure3dViewerRef } from "./components/measure-3d-viewer-container.tsx";
import MeasureData from "./components/measure-data.tsx";
import MeasureCard3DViewerActions from "./components/measure-actions-cards.tsx";
import rgbToHex from "@/utils/colors/rgb-to-hex.ts";
import LoadingButton from "@/components/buttons/loading-button";
import { ProjectHeadingState } from "@/pages/takeoff/context/project-heading/project-heading-context-props.ts";
import useStoreyMeasuresTools from "@/pages/takeoff/layouts/measures/layouts/storey-measures/hooks/use-storey-measures-tools.ts";

interface StoreyMeasuresDetailsComponentProps {
    alreadyLoadedRef: React.MutableRefObject<boolean>;
}

const StoreyMeasuresDetailsComponent: React.FC<StoreyMeasuresDetailsComponentProps> = ({ alreadyLoadedRef }) => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();

    const { isLoading, getLoader } = useViewerContext();
    const { building, storey, measuresRef, isDirty, isSaving, saveMeasures } = useStoreyMeasuresContext();
    const projectMeasureContext = useProjectMeasureContext();
    const {
        tempMeasureTypes,
        deletedMeasureTypeIds,
        storeysFileData,
        clearTempData,
        saveMeasureTypes,
        deleteMeasuresFromMeasureTypesAndStorey,
        getDrawingColorFromMeasureTypeId,
        getDrawingAlphaFromMeasureTypeId,
        updateProjectMeasuresFromMeasuresTypes,
    } = projectMeasureContext;

    const hasPendingMeasureTypes = tempMeasureTypes.length > 0 || deletedMeasureTypeIds.length > 0;

    const { setRoute } = useProjectFormNavigationContext();
    const { setIsNavigationPrevented, setSaveCallback } = usePreventNavigationContext();
    const { isInDrawingMode } = useModelingToolsContext();
    const { isMeasurementToolOpen } = useStoreyMeasurementContext();

    const isLockOwned = useMemo(
        () => canEdit,
        [canEdit],
    );
    const files = useMemo(() => storeysFileData.get(storey.id) ?? [], [storey.id, storeysFileData]);

    const { launchPlanDetectionJobs, dialogs } = useMeasureProjectActions();

    useEffect(() => {
        setIsNavigationPrevented(isDirty || isSaving || hasPendingMeasureTypes);
        return () => setIsNavigationPrevented(false);
    }, [isDirty, isSaving, hasPendingMeasureTypes, setIsNavigationPrevented]);

    const [transformedToMeasureData, setTransformedToMeasureData] = useState<
        TransformedToMeasureData[] | undefined
    >();

    const viewerRef = useRef<Measure3dViewerRef | null>(null);

    useEffect(() => {
        const setupTransformedToMeasureData = async () => {
            if (isLoading) return;
            const fragLoader = getLoader(FragmentLoader);
            const model = fragLoader?.getModel();
            if (model == null) return;
            const measuresBuffer = await model.getBuffer(true);
            setTransformedToMeasureData(
                new MeasureFragmentConverter().transformToMeasureData(new Uint8Array(measuresBuffer), true),
            );
        };
        setupTransformedToMeasureData();
    }, [getLoader, isLoading]);

    /* MEASURE DATA FUNCTIONS */

    const handleSave = useCallback(async (): Promise<boolean> => {
        if (!ensureCanEdit()) {
            return false;
        }

        if (viewerRef.current == null) return false;
        // The dialog stages its edit immediately before invoking this callback.
        // Read the live getters here, rather than the arrays captured at the last render.
        const pendingTypes = projectMeasureContext.tempMeasureTypes;
        const pendingDeletedIds = projectMeasureContext.deletedMeasureTypeIds;

        // D'abord supprimer les types de métré sur le serveur puis mettre à jour les types modifiés,
        // afin de recalculer ensuite les dataInformations des métrés impactés uniquement par des éditions.
        // Il n'est pas possible d'envoyer une seule requête avec les types de métrés modifiés, car il faut refaire un calcul des dataInformations.
        // Nécessaire, parce qu'il se peut que l'utilisateur ait des mesures en cours d'édition ou de nouvelles mesures qui ne sont pas encore enregistrées en BDD.

        // 1) Suppression des métrés à partir des types de métré marqués pour suppression et que pour l'étage en question.
        const deletedOk = await deleteMeasuresFromMeasureTypesAndStorey(storey.id);
        if (!deletedOk) {
            toast.error("Une erreur s'est produite lors de la suppression des types de métré.", {
                autoClose: false,
            });
            return false;
        }

        // 2) Sauvegarde des types de métré édités.
        const measureTypesSaved = await saveMeasureTypes();
        if (!measureTypesSaved) {
            toast.error("Une erreur s'est produite lors de l'enregistrement des types de métré.", {
                autoClose: false,
            });
            return false;
        }

        // Métrés dont le type de métré a été édité.
        const editedMeasureTypeIds = pendingTypes
            .map((type) => type.measureTypeId)
            .filter((id) => !pendingDeletedIds.includes(id));

        const currentMeasures = measuresRef.current;

        const measureIdsToRecalculate = new Set<string>(
            currentMeasures
                .filter((measure) => editedMeasureTypeIds.includes(measure.measureTypeId))
                .map((measure) => measure.measureId),
        );

        // Métrés dont le measureTypeId a été modifié par rapport aux données initiales de l'étage.
        const originalMeasuresMap = new Map(storey.measures.map((m) => [m.measureId, m.measureTypeId]));

        currentMeasures.forEach((measure) => {
            const originalTypeId = originalMeasuresMap.get(measure.measureId);
            if (originalTypeId !== undefined && originalTypeId !== measure.measureTypeId) {
                measureIdsToRecalculate.add(measure.measureId);
            }
        });

        // MAJ des métrés dans le FRAG.
        if (!transformedToMeasureData) {
            toast.error("Une erreur s'est produite lors de l'enregistrement.", {
                autoClose: false,
            });
            return false;
        }

        // Recalcul des dataInformations des métrés impactés, modification depuis le type (hors types supprimés).
        if (measureIdsToRecalculate.size > 0) {
            currentMeasures
                .filter((measure) => measureIdsToRecalculate.has(measure.measureId))
                .forEach((measure) => {
                    const measureData = transformedToMeasureData.find(
                        (md) => md.id === measure.geometryRepresentations[0].geometryId,
                    );
                    if (measureData == null) return;
                    measureData.dataInformations ??= { ...(measure.dataInformations ?? {}) };
                    const color = getDrawingColorFromMeasureTypeId(
                        measure.measureTypeId,
                    ).convertLinearToSRGB();
                    const alpha = getDrawingAlphaFromMeasureTypeId(measure.measureTypeId);
                    measureData.dataInformations.color = rgbToHex(color.r, color.g, color.b);
                    measureData.dataInformations.alpha = alpha;
                    measureData.color = color;
                    measureData.alpha = alpha;
                    viewerRef.current!.recalculateDataInformations(measure.measureTypeId, [measureData]);
                    measure.setDataInformations(measureData.dataInformations);
                });
        }

        const measuresFragModel = await viewerRef.current.save();
        const measuresBuffer = await measuresFragModel.getBuffer(false); // On envoie le buffer compressé

        const successfulSave = await saveMeasures(measuresBuffer);
        if (successfulSave) {
            // On met à jour TransformedToMeasureData à partir du nouveau buffer généré
            setTransformedToMeasureData(
                new MeasureFragmentConverter().transformToMeasureData(new Uint8Array(measuresBuffer), false),
            );

            // Mettre à jour aussi les métrés des autres étages concernés aux types de métré modifiés
            const successfulUpdate = await updateProjectMeasuresFromMeasuresTypes(
                [storey.id],
                editedMeasureTypeIds,
            );
            if (!successfulUpdate) {
                toast.warning(
                    "Métrés enregistrés partiellement. Impossible d'appliquer la modification de types de métré sur l’ensemble du projet.",
                    {
                        autoClose: false,
                    },
                );
                return false;
            }

            // Only clear pending changes once every affected storey has been updated.
            // A failed propagation must remain retryable from the save button.
            if (pendingTypes.length > 0 || pendingDeletedIds.length > 0)
                await projectMeasureContext.invalidateMeasureTypes();
            toast.success("Métrés enregistrés avec succès", {
                autoClose: 3000,
            });
            return true;
        }

        toast.error("Une erreur s'est produite lors de l'enregistrement.", {
            autoClose: false,
        });
        return false;
    }, [
        ensureCanEdit,
        projectMeasureContext,
        deleteMeasuresFromMeasureTypesAndStorey,
        storey.id,
        storey.measures,
        saveMeasureTypes,
        measuresRef,
        transformedToMeasureData,
        saveMeasures,
        getDrawingColorFromMeasureTypeId,
        getDrawingAlphaFromMeasureTypeId,
        updateProjectMeasuresFromMeasuresTypes,
    ]);

    useEffect(() => {
        setSaveCallback(handleSave);
        return () => {
            setSaveCallback(undefined);
        };
    }, [handleSave, setSaveCallback]);

    const getTransformedToMeasureDataFromGeometryId = useCallback(
        (geometryId: string) => transformedToMeasureData?.find((md) => md.id === geometryId),
        [transformedToMeasureData],
    );

    const heading = useMemo<ProjectHeadingState>(
        () => ({
            title: (
                <div className="flex flex-row gap-x-1.5 items-center">
                    <span className="font-semibold">{isLockOwned ? "Edition" : "Visualisation"}</span>
                    <span>:</span>
                    <span>{building.name}</span>
                    <FontAwesomeIcon icon={faChevronRight} className="text-sm text-gray-600" />
                    <span>{storey.name}</span>
                    <FontAwesomeIcon icon={faChevronRight} className="text-sm text-gray-600" />
                    <span className="text-gray-500">{files[0]?.name}</span>
                </div>
            ),
            left: (
                <div className="flex flex-row gap-x-2">
                    <Button
                        variant="simple"
                        className="pointer-events-auto flex flex-row gap-y-2 cursor-pointer"
                        disabled={isSaving || isInDrawingMode || isMeasurementToolOpen}
                        onClick={() => {
                            clearTempData();
                            setRoute("measures");
                        }}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>

                    {isLockOwned && (
                        <LoadingButton
                            onClick={handleSave}
                            isLoading={isSaving}
                            size="sm"
                            disabled={
                                isInDrawingMode ||
                                isMeasurementToolOpen ||
                                (!isDirty && !hasPendingMeasureTypes)
                            }
                            body={<FontAwesomeIcon icon={faSave} className="text-sm" />}
                        />
                    )}
                </div>
            ),
        }),
        [
            isLockOwned,
            building.name,
            storey.name,
            files,
            isSaving,
            isInDrawingMode,
            isMeasurementToolOpen,
            handleSave,
            isDirty,
            hasPendingMeasureTypes,
            clearTempData,
            setRoute,
        ],
    );

    useProjectFormHeadingLayoutEffect(heading);

    return (
        <div className="relative h-full w-full overflow-hidden">
            <MeasureData
                viewerRef={viewerRef}
                handleSave={handleSave}
                getTransformedToMeasureDataFromGeometryId={getTransformedToMeasureDataFromGeometryId}
            />

            <MeasureCard3DViewerActions onLaunchPlanDetection={launchPlanDetectionJobs} />

            <Measure3dViewerContainer ref={viewerRef} alreadyLoadedRef={alreadyLoadedRef} />

            {dialogs}
        </div>
    );
};

const StoreyMeasuresContent: React.FC = () => {
    const viewerLoaders = useStoreyMeasuresLoaders();
    const tools = useStoreyMeasuresTools();

    const alreadyLoadedRef = useRef<boolean>(false);

    const handleAllLoadersLoaded = useCallback(() => {
        alreadyLoadedRef.current = true;
    }, []);

    return (
        <ViewerContextProvider
            loaders={viewerLoaders}
            tools={tools}
            onAllLoadersLoaded={handleAllLoadersLoaded}
        >
            <StoreyMeasuresDetailsComponent alreadyLoadedRef={alreadyLoadedRef} />
        </ViewerContextProvider>
    );
};

interface StoreyMeasuresDetailsProps {
    storeyId: string;
}

const StoreyMeasuresDetails: React.FC<StoreyMeasuresDetailsProps> = ({ storeyId }) => {
    const { isLoading } = useProjectMeasureContext();

    if (isLoading) return <SimpleLoadingIndicator />;

    return (
        <StoreyMeasuresProviders storeyId={storeyId}>
            <StoreyIndicatorsProvider storeyId={storeyId}>
                <ViewerItemsVisibilityProvider>
                    <ModelingToolsContextProvider>
                        <StoreyMeasurementProvider>
                            <StoreyMeasuresContent />
                        </StoreyMeasurementProvider>
                    </ModelingToolsContextProvider>
                </ViewerItemsVisibilityProvider>
            </StoreyIndicatorsProvider>
        </StoreyMeasuresProviders>
    );
};

export default StoreyMeasuresDetails;
